
import { useEffect } from "react";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import BackToTopButton from "../components/BackToTopButton";
import FloatingSocialButtons from "../components/FloatingSocialButtons";
import { Code, Globe, Smartphone, Zap, Shield, Palette, Database, Cloud } from "lucide-react";

const WebDevelopmentPage = () => {
  useEffect(() => {
    document.title = "Web Development - DG IT Pvt. Ltd.";
    window.scrollTo(0, 0);
  }, []);

  const services = [
    {
      icon: <Globe className="w-8 h-8 text-blue-600" />,
      title: "Custom Website Development",
      description: "Tailored websites built from scratch to meet your specific business requirements and goals"
    },
    {
      icon: <Smartphone className="w-8 h-8 text-blue-600" />,
      title: "Responsive Design",
      description: "Mobile-first approach ensuring your website looks perfect on all devices and screen sizes"
    },
    {
      icon: <Code className="w-8 h-8 text-blue-600" />,
      title: "Frontend Development",
      description: "Modern, interactive user interfaces using React, Vue.js, and other cutting-edge technologies"
    },
    {
      icon: <Database className="w-8 h-8 text-blue-600" />,
      title: "Backend Development",
      description: "Robust server-side solutions with secure APIs and efficient database management"
    },
    {
      icon: <Zap className="w-8 h-8 text-blue-600" />,
      title: "Performance Optimization",
      description: "Lightning-fast loading speeds and optimal performance for better user experience"
    },
    {
      icon: <Shield className="w-8 h-8 text-blue-600" />,
      title: "Security Implementation",
      description: "Advanced security measures to protect your website and user data from threats"
    },
    {
      icon: <Palette className="w-8 h-8 text-blue-600" />,
      title: "UI/UX Design",
      description: "Beautiful, intuitive designs that enhance user engagement and conversion rates"
    },
    {
      icon: <Cloud className="w-8 h-8 text-blue-600" />,
      title: "Cloud Deployment",
      description: "Scalable hosting solutions and deployment on reliable cloud platforms"
    }
  ];

  const technologies = [
    { name: "React", icon: "⚛️" },
    { name: "Node.js", icon: "🟢" },
    { name: "Next.js", icon: "▲" },
    { name: "Vue.js", icon: "💚" },
    { name: "MongoDB", icon: "🍃" },
    { name: "PostgreSQL", icon: "🐘" },
    { name: "AWS", icon: "☁️" },
    { name: "Docker", icon: "🐳" }
  ];

  const features = [
    "Search Engine Optimization (SEO)",
    "Content Management Systems (CMS)",
    "E-commerce Integration",
    "Payment Gateway Integration",
    "Social Media Integration",
    "Analytics and Reporting",
    "Multi-language Support",
    "Progressive Web Apps (PWA)"
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <meta name="description" content="Professional web development services in Nepal. Custom websites, responsive design, e-commerce solutions, and modern web applications." />
        <meta name="keywords" content="web development, website design, e-commerce, responsive design, React, Node.js, Nepal" />
        <meta property="og:title" content="Web Development - DG IT Pvt. Ltd." />
      </Helmet>
      
      <Navbar />
      <main className="flex-grow pt-24">
        <div className="container mx-auto px-6 py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <h1 className="text-4xl md:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-blue-800 mb-4">
              Web Development Services
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Transform your business with cutting-edge web solutions that drive growth and engage your audience.
            </p>
          </motion.div>

          {/* Services Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="glass-card rounded-xl p-6 hover:shadow-xl transition-shadow"
              >
                <div className="mb-4">{service.icon}</div>
                <h3 className="text-xl font-bold mb-2">{service.title}</h3>
                <p className="text-gray-600">{service.description}</p>
              </motion.div>
            ))}
          </div>

          {/* Technologies Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="bg-gray-50 rounded-2xl p-8 mb-16"
          >
            <h2 className="text-3xl font-bold text-center mb-8">Technologies We Use</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {technologies.map((tech, index) => (
                <div key={index} className="bg-white rounded-lg p-4 text-center shadow-md hover:shadow-lg transition-shadow">
                  <div className="text-3xl mb-2">{tech.icon}</div>
                  <h3 className="font-semibold">{tech.name}</h3>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Features Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="mb-16"
          >
            <h2 className="text-3xl font-bold text-center mb-8">Additional Features</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {features.map((feature, index) => (
                <div key={index} className="flex items-center space-x-3 p-3 bg-blue-50 rounded-lg">
                  <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                  <span className="font-medium">{feature}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Process Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="mb-16"
          >
            <h2 className="text-3xl font-bold text-center mb-12">Our Development Process</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {[
                { step: "01", title: "Discovery", desc: "Understanding your requirements and goals" },
                { step: "02", title: "Design", desc: "Creating wireframes and visual designs" },
                { step: "03", title: "Development", desc: "Building your website with modern technologies" },
                { step: "04", title: "Launch", desc: "Testing, deployment, and ongoing support" }
              ].map((item, index) => (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                    {item.step}
                  </div>
                  <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                  <p className="text-gray-600">{item.desc}</p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-2xl p-10 text-white text-center">
            <h2 className="text-3xl font-bold mb-4">Ready to Build Your Dream Website?</h2>
            <p className="text-xl text-blue-100 mb-6">
              Let's create a powerful web presence that drives your business forward.
            </p>
            <motion.a
              href="/quote"
              whileHover={{ scale: 1.05 }}
              className="inline-block px-8 py-4 bg-white text-blue-600 font-bold rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
            >
              Get Started Now
            </motion.a>
          </div>
        </div>
      </main>
      <Footer />
      <BackToTopButton />
      <FloatingSocialButtons />
    </div>
  );
};

export default WebDevelopmentPage;
